'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface VolunteerApplicationModalProps {
  opportunityId: number;
  opportunityTitle: string;
  location: string;
  timeCommitment: string;
  requiredSkills: string[];
  onSubmit: (data: {
    fullName: string;
    email: string;
    phone?: string;
    opportunityId: number;
    opportunityTitle: string;
    location: string;
    skills: string[];
    motivation?: string;
    availability?: string;
  }) => void;
  onCancel: () => void;
}

const VolunteerApplicationModal: React.FC<VolunteerApplicationModalProps> = ({
  opportunityId,
  opportunityTitle,
  location,
  timeCommitment,
  requiredSkills,
  onSubmit,
  onCancel,
}) => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    skills: [] as string[],
    motivation: '',
    availability: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSkillToggle = (skill: string) => {
    setFormData((prev) => ({
      ...prev,
      skills: prev.skills.includes(skill)
        ? prev.skills.filter((s) => s !== skill)
        : [...prev.skills, skill],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (formData.skills.length === 0) {
      alert('Please select at least one skill');
      return;
    }

    setIsSubmitting(true);

    try {
      await onSubmit({
        fullName: formData.fullName,
        email: formData.email,
        phone: formData.phone || undefined,
        opportunityId,
        opportunityTitle,
        location,
        skills: formData.skills,
        motivation: formData.motivation || undefined,
        availability: formData.availability || undefined,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-6 overflow-y-auto">
      <div className="bg-card rounded-lg max-w-2xl w-full p-8 relative my-8">
        <button
          onClick={onCancel}
          className="absolute top-4 right-4 w-10 h-10 bg-muted rounded-full flex items-center justify-center hover:bg-muted/80 transition-colors"
          aria-label="Close modal"
        >
          <Icon name="XMarkIcon" size={24} className="text-foreground" />
        </button>

        <div className="mb-6">
          <h2 className="text-3xl font-headline font-bold text-foreground mb-2">
            Apply for {opportunityTitle}
          </h2>
          <p className="text-muted-foreground">
            Join our volunteer team and make a meaningful impact in the peace movement.
          </p>
        </div>

        <div className="mb-6 p-4 bg-primary/10 rounded-lg space-y-2">
          <div className="flex items-center space-x-2">
            <Icon name="MapPinIcon" size={16} className="text-primary" />
            <span className="text-foreground font-medium">{location}</span>
          </div>
          <div className="flex items-center space-x-2">
            <Icon name="ClockIcon" size={16} className="text-primary" />
            <span className="text-foreground font-medium">{timeCommitment}</span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="fullName" className="block text-sm font-medium text-foreground mb-2">
              Full Name <span className="text-destructive">*</span>
            </label>
            <input
              type="text"
              id="fullName"
              required
              value={formData.fullName}
              onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
              className="w-full px-4 py-3 bg-background border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Enter your full name"
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
              Email Address <span className="text-destructive">*</span>
            </label>
            <input
              type="email"
              id="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-4 py-3 bg-background border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="your.email@example.com"
            />
          </div>

          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-foreground mb-2">
              Phone Number (Optional)
            </label>
            <input
              type="tel"
              id="phone"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="w-full px-4 py-3 bg-background border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="+1 (555) 000-0000"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Your Skills <span className="text-destructive">*</span>
            </label>
            <div className="flex flex-wrap gap-2">
              {requiredSkills.map((skill) => (
                <button
                  key={skill}
                  type="button"
                  onClick={() => handleSkillToggle(skill)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                    formData.skills.includes(skill)
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-foreground hover:bg-muted/80'
                  }`}
                >
                  {skill}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label htmlFor="availability" className="block text-sm font-medium text-foreground mb-2">
              Availability (Optional)
            </label>
            <input
              type="text"
              id="availability"
              value={formData.availability}
              onChange={(e) => setFormData({ ...formData, availability: e.target.value })}
              className="w-full px-4 py-3 bg-background border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="e.g., Weekends, 10-15 hours/week"
            />
          </div>

          <div>
            <label htmlFor="motivation" className="block text-sm font-medium text-foreground mb-2">
              Why do you want to volunteer? (Optional)
            </label>
            <textarea
              id="motivation"
              rows={4}
              value={formData.motivation}
              onChange={(e) => setFormData({ ...formData, motivation: e.target.value })}
              className="w-full px-4 py-3 bg-background border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              placeholder="Share your motivation for joining this volunteer opportunity..."
            />
          </div>

          <div className="flex items-center space-x-4 pt-4">
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-6 py-3 bg-primary text-primary-foreground rounded-md font-cta font-bold transition-all duration-300 hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Application'}
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="px-6 py-3 bg-muted text-foreground rounded-md font-medium transition-all duration-300 hover:bg-muted/80"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VolunteerApplicationModal;